import { useEffect, useState, useCallback, useMemo } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import FileUpload from "@/shared/components/FileUpload";
import {
  Ruler, CheckCircle2, Clock, Send, Sparkles, ArrowLeft, ChevronRight, ChevronDown,
  FolderOpen, ImageIcon, MessageSquare, Layers, X, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/shared/lib/auth";

/* Pre-Audit Portal (Msg 906) — the site pre-audit user (assigned by the PSM)
   records, per wall/glass: the on-site measurement, photos and a condition
   comment. Project-grouped master-detail, mirroring the Designer Portal so it
   scales to many line items. Privacy: project name + code only. */

const STATE = {
  pending:   { label: "To measure",        dot: "bg-amber-400",   chip: "bg-amber-100 text-amber-800 border-amber-300" },
  submitted: { label: "Awaiting PSM",      dot: "bg-blue-400",    chip: "bg-blue-100 text-blue-700 border-blue-300" },
  approved:  { label: "Approved by PSM",   dot: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  changes:   { label: "Changes requested", dot: "bg-rose-400",    chip: "bg-rose-100 text-rose-700 border-rose-300" },
};
const NEEDS_WORK = (s) => s === "pending" || s === "changes";

export default function PreAuditPortal() {
  const { user } = useAuth();
  const [scopes, setScopes] = useState(null);
  const [view, setView] = useState({ code: null, bid: null });

  const load = useCallback(() => {
    api.get("/my-preaudit")
      .then((r) => setScopes(r.data.scopes || []))
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"); setScopes([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const projects = useMemo(() => {
    const map = new Map();
    (scopes || []).forEach((s) => {
      if (!map.has(s.project_code)) map.set(s.project_code, { code: s.project_code, name: s.project_name || s.project_code, walls: [] });
      map.get(s.project_code).walls.push(s);
    });
    return Array.from(map.values()).map((p) => ({
      ...p, todo: p.walls.filter((s) => NEEDS_WORK(s.status)).length, done: p.walls.filter((s) => !NEEDS_WORK(s.status)).length,
    })).sort((a, b) => b.todo - a.todo || a.name.localeCompare(b.name));
  }, [scopes]);

  const active = view.code ? projects.find((p) => p.code === view.code) : null;
  const openProject = (code) => {
    const p = projects.find((x) => x.code === code);
    const first = p?.walls.find((s) => NEEDS_WORK(s.status)) || p?.walls[0];
    setView({ code, bid: first?.boq_id || null });
  };

  return (
    <div className="p-5 sm:p-8 max-w-6xl mx-auto" data-testid="preaudit-portal">
      {scopes === null ? (
        <div className="py-24 text-center text-zinc-400">Loading…</div>
      ) : active ? (
        <ProjectView project={active}
                     onBack={() => setView({ code: null, bid: null })} onDone={load} />
      ) : (
        <Dashboard projects={projects} scopes={scopes} userName={user?.name} onOpen={openProject} />
      )}
    </div>
  );
}

function Dashboard({ projects, scopes, userName, onOpen }) {
  const todo = scopes.filter((s) => NEEDS_WORK(s.status)).length;
  return (
    <>
      <div className="mb-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-teal-600 font-semibold">Site Pre-Audit</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          {userName ? `Welcome, ${userName.split(" ")[0]}` : "Pre-audit"}
        </h1>
        <p className="text-zinc-500 mt-2 text-sm sm:text-base max-w-2xl">
          For each wall/glass: take the on-site measurement, a photo, and note the surface condition. The PSM reviews what you submit.
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <Stat label="To measure" value={todo} tone="amber" />
          <Stat label="Walls" value={scopes.length} tone="teal" />
          <Stat label="Projects" value={projects.length} tone="zinc" />
        </div>
      </div>
      {projects.length === 0 ? (
        <div className="py-24 text-center" data-testid="pa-empty">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-teal-50 mb-4">
            <Sparkles className="w-7 h-7 text-teal-400" strokeWidth={1.5} />
          </div>
          <div className="font-display text-xl font-bold">No projects assigned yet</div>
          <p className="text-zinc-500 mt-1 text-sm">When the PSM assigns you to a site, it shows up here.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((p) => (
            <button key={p.code} onClick={() => onOpen(p.code)} data-testid={`pa-project-card-${p.code}`}
                    className="group text-left bg-white border border-zinc-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:-translate-y-1 hover:border-teal-300 transition-all">
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-teal-50 text-teal-600">
                  <FolderOpen className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-teal-500 transition-colors" strokeWidth={1.5} />
              </div>
              <div className="font-display text-lg font-bold tracking-tight mt-4 truncate">{p.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{p.code}</div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {p.todo > 0 && <Pill tone="amber">{p.todo} to measure</Pill>}
                {p.done > 0 && <Pill tone="emerald">{p.done} done</Pill>}
                <Pill tone="zinc">{p.walls.length} wall{p.walls.length !== 1 ? "s" : ""}</Pill>
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function buildGroups(walls, needsWork) {
  const map = new Map();
  for (const w of walls) { const g = w.group || ""; if (!map.has(g)) map.set(g, []); map.get(g).push(w); }
  const entries = [...map.entries()].sort((a, b) =>
    a[0] === "" ? 1 : b[0] === "" ? -1 : a[0].localeCompare(b[0]));
  return entries.map(([name, ws]) => ({
    key: name || "__ungrouped__", name, walls: ws,
    todo: ws.filter(needsWork).length,
  }));
}

function ProjectView({ project, onBack, onDone }) {
  const groups = useMemo(() => buildGroups(project.walls, (s) => NEEDS_WORK(s.status)), [project.walls]);
  const noGroups = groups.length === 1 && groups[0].name === "";
  const [gkey, setGkey] = useState(() => (groups.find((g) => g.todo > 0) || groups[0])?.key);
  const active = groups.find((g) => g.key === gkey) || groups[0];

  return (
    <div data-testid={`pa-project-view-${project.code}`}>
      <button onClick={onBack} data-testid="pa-back-btn"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-teal-600 transition-colors mb-4">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> All projects
      </button>
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-teal-600 font-semibold">{project.code}</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">{project.name}</h1>
        <p className="text-zinc-500 mt-1 text-sm">
          {noGroups ? `${project.walls.length} wall${project.walls.length !== 1 ? "s" : ""} to pre-audit.`
            : `Pick a group on the left — its walls open on the right to measure.`}
        </p>
      </div>
      <div className="grid lg:grid-cols-[minmax(240px,300px)_1fr] gap-6 items-start">
        <GroupRail groups={groups} active={active?.key} onSelect={setGkey} noGroups={noGroups} />
        <div className="space-y-4" data-testid={`pa-group-walls-${active?.key}`}>
          {!noGroups && (
            <div className="flex items-center gap-2 px-1">
              <Layers className="w-4 h-4 text-teal-600" strokeWidth={1.5} />
              <h2 className="font-display text-lg font-bold tracking-tight">{active?.name || "Ungrouped"}</h2>
              <span className="text-xs text-zinc-400">· {active?.walls.length} wall{active?.walls.length !== 1 ? "s" : ""}</span>
            </div>
          )}
          {(active?.walls || []).map((s) => (
            <WallForm key={s.boq_id} scope={s} onDone={onDone} />
          ))}
        </div>
      </div>
    </div>
  );
}

function GroupRail({ groups, active, onSelect, noGroups }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100 overflow-hidden lg:sticky lg:top-20" data-testid="pa-group-rail">
      {groups.map((g) => (
        <button key={g.key} onClick={() => onSelect(g.key)} data-testid={`pa-group-row-${g.key}`}
                className={`w-full flex items-center gap-3 px-3.5 py-3 text-left transition-colors ${g.key === active ? "bg-teal-50/70" : "hover:bg-zinc-50"}`}>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${g.name ? "bg-teal-50 text-teal-600" : "bg-zinc-100 text-zinc-400"}`}>
            <Layers className="w-4 h-4" strokeWidth={1.5} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-zinc-900 truncate">{noGroups ? "All walls" : (g.name || "Ungrouped")}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {g.walls.length} wall{g.walls.length !== 1 ? "s" : ""}
              {g.todo > 0 && <span className="text-amber-600"> · {g.todo} to measure</span>}
            </div>
          </div>
          <ChevronRight className={`w-4 h-4 flex-shrink-0 ${g.key === active ? "text-teal-500" : "text-zinc-300"}`} strokeWidth={1.5} />
        </button>
      ))}
    </div>
  );
}

function WallForm({ scope, onDone }) {
  const meta = STATE[scope.status] || STATE.pending;
  const isArea = scope.is_area;
  const [h, setH] = useState(scope.height || "");
  const [w, setW] = useState(scope.width || "");
  const [u, setU] = useState(scope.measure_unit || "in");
  const [qty, setQty] = useState(scope.total_quantity || "");
  const [photos, setPhotos] = useState(scope.photos || []);
  const [comment, setComment] = useState(scope.comment || "");
  const [newPhoto, setNewPhoto] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [open, setOpen] = useState(NEEDS_WORK(scope.status));  // collapse done walls by default

  function addPhoto(url) { if (url) { setPhotos((p) => [...p, url]); setNewPhoto(""); } }

  async function submit() {
    if (photoBusy) { toast.error("Wait — a site photo is still uploading."); return; }
    if (photos.length === 0) { toast.error("A site photo is required. Upload at least one photo."); return; }
    if (isArea && (!h || !w)) { toast.error("Enter length and breadth"); return; }
    setSaving(true);
    try {
      const body = isArea
        ? { height: parseFloat(h), width: parseFloat(w), unit: u, photos, comment: comment.trim() }
        : { quantity: parseFloat(qty || "1"), photos, comment: comment.trim() };
      const r = await api.post(`/projects/${scope.project_code}/boq/${scope.boq_id}/preaudit`, body);
      const ex = r.data.measured_sqft && r.data.measured !== r.data.measured_sqft ? ` (≈ ${r.data.measured_sqft} sqft)` : "";
      toast.success(`Submitted: ${r.data.measured} ${scope.unit}${ex}`);
      onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  }

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden" data-testid={`pa-wall-form-${scope.boq_id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`pa-wall-toggle-${scope.boq_id}`}
              className="w-full p-4 sm:p-5 flex items-start justify-between gap-4 text-left hover:bg-zinc-50/60 transition-colors">
        <div className="min-w-0 flex items-start gap-3">
          <ChevronDown className={`w-4 h-4 text-zinc-400 shrink-0 mt-1 transition-transform ${open ? "" : "-rotate-90"}`} strokeWidth={1.5} />
          <div className="min-w-0">
            {scope.wall_label && <div className="font-mono text-[11px] uppercase tracking-widest text-teal-500">{scope.wall_label}</div>}
            <h3 className="font-display text-base sm:text-lg font-bold tracking-tight mt-0.5 truncate">{scope.title}</h3>
            <div className="text-xs text-zinc-500 mt-1">Scope unit: <b>{scope.unit || "—"}</b></div>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] font-medium border whitespace-nowrap ${meta.chip}`} data-testid={`pa-status-${scope.boq_id}`}>
          {scope.status === "approved" ? <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} />
            : scope.status === "changes" ? <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.5} />
            : <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />} {meta.label}
        </span>
      </button>

      {open && (<>
      {scope.status === "changes" && scope.review_note && (
        <div className="mx-5 sm:mx-6 mt-4 p-3 rounded-lg bg-rose-50 border border-rose-200" data-testid={`pa-change-note-${scope.boq_id}`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-rose-600 font-semibold">PSM asked for changes</div>
          <p className="text-sm text-rose-800 mt-1">{scope.review_note}</p>
        </div>
      )}

      {!NEEDS_WORK(scope.status) ? (
        /* LOCKED — measurements are submitted; read-only until the PSM
           requests changes. No more editable fields / "Update" button. */
        <div className="p-5 sm:p-6 space-y-5" data-testid={`pa-locked-${scope.boq_id}`}>
          <div className={`flex items-center gap-2 text-sm rounded-lg px-4 py-3 border
            ${scope.status === "approved"
              ? "text-emerald-700 bg-emerald-50 border-emerald-200"
              : "text-blue-700 bg-blue-50 border-blue-200"}`}>
            <Lock className="w-4 h-4" strokeWidth={1.5} />
            {scope.status === "approved"
              ? "Approved by the PSM and locked. Nothing more to do here."
              : "Submitted to the PSM and locked. You'll be notified if any change is needed."}
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 mb-2 flex items-center gap-1">
              <Ruler className="w-3.5 h-3.5" strokeWidth={1.5} /> Recorded measurement
            </div>
            <div className="text-lg font-display font-bold text-zinc-900" data-testid={`pa-recorded-${scope.boq_id}`}>
              {isArea
                ? `${scope.height || "—"} × ${scope.width || "—"} ${scope.measure_unit || "in"}`
                : `${scope.total_quantity ?? "—"} ${scope.unit || ""}`}
            </div>
          </div>

          {photos.length > 0 && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 mb-2 flex items-center gap-1">
                <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.5} /> Site photos
              </div>
              <div className="flex flex-wrap gap-2">
                {photos.map((p, i) => (
                  <a key={i} href={p} target="_blank" rel="noreferrer"
                     className="block w-20 h-16 rounded-lg overflow-hidden border border-zinc-200 hover:ring-2 hover:ring-teal-300 transition">
                    <img src={p} alt="site" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                  </a>
                ))}
              </div>
            </div>
          )}

          {comment && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 mb-2 flex items-center gap-1">
                <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.5} /> Condition comment
              </div>
              <p className="text-sm text-zinc-700 whitespace-pre-line">{comment}</p>
            </div>
          )}
        </div>
      ) : (
      <div className="p-5 sm:p-6 space-y-5">
        {/* Measurement */}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 mb-2 flex items-center gap-1">
            <Ruler className="w-3.5 h-3.5" strokeWidth={1.5} /> Measurement
          </div>
          {isArea ? (
            <div className="grid grid-cols-3 gap-3">
              <LabeledInput label="Length" v={h} set={setH} tid={`pa-length-${scope.boq_id}`} />
              <LabeledInput label="Breadth" v={w} set={setW} tid={`pa-breadth-${scope.boq_id}`} />
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Unit</label>
                <select value={u} onChange={(e) => setU(e.target.value)} data-testid={`pa-unit-${scope.boq_id}`}
                        className="mt-1 w-full h-10 rounded-lg border border-zinc-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300">
                  <option value="in">in</option><option value="ft">ft</option><option value="cm">cm</option><option value="m">m</option>
                </select>
              </div>
            </div>
          ) : (
            <LabeledInput label={`Quantity (${scope.unit})`} v={qty} set={setQty} tid={`pa-qty-${scope.boq_id}`} />
          )}
          {isArea && scope.is_sqm && <div className="text-[11px] text-zinc-400 mt-1.5">Measure in ft — auto-converts to {scope.unit}.</div>}
        </div>

        {/* Photos */}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 mb-2 flex items-center gap-1">
            <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.5} /> Site photos
          </div>
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {photos.map((p, i) => (
                <div key={i} className="relative w-20 h-16 rounded-lg overflow-hidden border border-zinc-200 group">
                  <img src={p} alt="site" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                  <button onClick={() => setPhotos((ph) => ph.filter((_, j) => j !== i))} data-testid={`pa-photo-remove-${scope.boq_id}-${i}`}
                          className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition">
                    <X className="w-3 h-3" strokeWidth={2} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <FileUpload value={newPhoto} onChange={addPhoto} onBusyChange={setPhotoBusy} label="Add a site photo" accept=".png,.jpg,.jpeg,.webp" testid={`pa-photo-${scope.boq_id}`} />
        </div>

        {/* Comment */}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 mb-2 flex items-center gap-1">
            <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.5} /> Condition comment
          </div>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
                    placeholder="e.g. Surface uneven near top-left; needs levelling before install."
                    className="w-full text-sm rounded-lg border border-zinc-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-300"
                    data-testid={`pa-comment-${scope.boq_id}`} />
        </div>

        <button onClick={submit} disabled={saving || photoBusy || photos.length === 0} data-testid={`pa-submit-${scope.boq_id}`}
                className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-teal-600 text-white font-medium shadow-sm hover:bg-teal-700 transition-all hover:-translate-y-0.5 disabled:opacity-50">
          <Send className="w-4 h-4" strokeWidth={1.5} /> {photoBusy ? "Photo uploading…" : photos.length === 0 ? "Add a site photo to submit" : saving ? "Submitting…" : scope.status === "changes" ? "Resubmit pre-audit" : "Submit pre-audit"}
        </button>
      </div>
      )}
      </>)}
    </div>
  );
}

function LabeledInput({ label, v, set, tid }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{label}</label>
      <input type="number" step="0.01" value={v} onChange={(e) => set(e.target.value)} data-testid={tid}
             className="mt-1 w-full h-10 rounded-lg border border-zinc-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300" />
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = { amber: "bg-amber-50 text-amber-800 border-amber-200", teal: "bg-teal-50 text-teal-800 border-teal-200", zinc: "bg-zinc-100 text-zinc-700 border-zinc-200" }[tone];
  return <span className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-medium border ${cls}`}><span className="font-bold tabular-nums">{value}</span> {label}</span>;
}
function Pill({ tone, children }) {
  const cls = { amber: "bg-amber-100 text-amber-800", emerald: "bg-emerald-100 text-emerald-700", zinc: "bg-zinc-100 text-zinc-600" }[tone];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>{children}</span>;
}
